import React, { useCallback, useRef } from 'react';
import { useClickStore } from '../store/clickStore';
import { useShallow } from 'zustand/react/shallow';
import { ToolSubmenu } from '../components/ui/ToolSubmenu';
import MeasurementList from '../components/ui/MeasurementList';
import { getLevelLabels } from '../config/siteConfig';
import { useSectionStore } from '../store/sectionStore';
import { useStringLayerStore } from '../store/stringLayerStore';
import { parseStringFile, stringsToCsv, stringsToDxf } from '../Utils/stringIo';
import { downloadBlob } from '../Utils/jpegPdf';

const ToolBtn = ({ active, title, onClick, children, layout }) => (
  <button
    type="button"
    title={title}
    onClick={onClick}
    className={`h-7 px-2 m-0 inline-flex items-center gap-1.5 rounded-sm border text-[10px] font-semibold uppercase tracking-wider transition-colors shrink-0 ${
      layout === 'column' ? 'w-full justify-start' : ''
    } ${
      active
        ? 'border-yellow-400 bg-yellow-500 text-black'
        : 'border-yellow-600/30 bg-zinc-950 text-yellow-400 hover:border-yellow-400 hover:bg-zinc-900'
    }`}
  >
    {children}
  </button>
);

/** Scene tools submenu (row under top bar, or column on the right). Shortcuts live in useSceneToolShortcuts. */
export default function Measurements({
  showMeasurements,
  layout = 'row',
  onClose,
  showHelp,
  onHelp,
  flyAround,
  onToggleFlyAround,
  showGrid,
  onToggleGrid,
}) {
  const { clickType, setClickType, setZoomToExtent, setLabels, showLabels, planLocked, togglePlanLocked, snapEnabled, toggleSnapEnabled } = useClickStore(
    useShallow((state) => ({
      clickType: state.clickType,
      setClickType: state.setClickType,
      setZoomToExtent: state.setZoomToExtent,
      setLabels: state.setLabels,
      showLabels: state.showLabels,
      planLocked: state.planLocked,
      togglePlanLocked: state.togglePlanLocked,
      snapEnabled: state.snapEnabled,
      toggleSnapEnabled: state.toggleSnapEnabled,
    }))
  );

  const sectionPanelOpen = useSectionStore((s) => s.panelOpen);
  const sectionEnabled = useSectionStore((s) => s.enabled);
  const digitizing = useStringLayerStore((s) => s.digitizing);
  const stringCount = useStringLayerStore((s) => s.strings.length);
  const fileRef = useRef(null);

  const handleButtonClick = useCallback(
    (id) => {
      setClickType(clickType === id && id !== 'pan' ? 'pan' : id);
    },
    [clickType, setClickType]
  );

  if (!showMeasurements) return null;

  return (
    <ToolSubmenu layout={layout} label="Tools" onClose={onClose}>
      <ToolBtn layout={layout} title="Zoom to extent (E)" onClick={() => setZoomToExtent(true)}>
        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.7">
          <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className={layout === 'column' ? 'inline' : 'hidden sm:inline'}>Extent</span>
      </ToolBtn>

      <ToolBtn layout={layout} title="Pan (P)" active={clickType === 'pan'} onClick={() => handleButtonClick('pan')}>
        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.7">
          <path d="M12 4v16M4 12h16M8 8l4-4 4 4M8 16l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className={layout === 'column' ? 'inline' : 'hidden sm:inline'}>Pan</span>
      </ToolBtn>

      <ToolBtn layout={layout} title="Info (I)" active={clickType === 'info'} onClick={() => handleButtonClick('info')}>
        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.7">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 10v6M12 7h.01" strokeLinecap="round" />
        </svg>
        <span className={layout === 'column' ? 'inline' : 'hidden sm:inline'}>Info</span>
      </ToolBtn>

      <ToolBtn
        layout={layout}
        title="Distance (D)"
        active={clickType === 'distance'}
        onClick={() => handleButtonClick('distance')}
      >
        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.7">
          <path d="M4 20L20 4M7 20h4M13 14h4M4 13v4" strokeLinecap="round" />
        </svg>
        <span className={layout === 'column' ? 'inline' : 'hidden sm:inline'}>Distance</span>
      </ToolBtn>

      <ToolBtn layout={layout} title="Zoom (Z)" active={clickType === 'zoom'} onClick={() => handleButtonClick('zoom')}>
        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.7">
          <circle cx="10.5" cy="10.5" r="6.5" />
          <path d="M16 16l4 4M10.5 8v5M8 10.5h5" strokeLinecap="round" />
        </svg>
        <span className={layout === 'column' ? 'inline' : 'hidden sm:inline'}>Zoom</span>
      </ToolBtn>

      <ToolBtn layout={layout} title="Color (C)" active={clickType === 'color'} onClick={() => handleButtonClick('color')}>
        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.7">
          <path
            d="M12 3a9 9 0 109 9c0-1.5-1.2-2-2.2-2-.9 0-1.5.5-2.3.5S15 9.5 15 8.5 15.7 7 17 7"
            strokeLinecap="round"
          />
        </svg>
        <span className={layout === 'column' ? 'inline' : 'hidden sm:inline'}>Colour</span>
      </ToolBtn>

      <ToolBtn layout={layout} title="Hide layer (H) — click an object to turn it off" active={clickType === 'hide'} onClick={() => handleButtonClick('hide')}>
        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.7">
          <path d="M3 3l18 18M10.6 10.6A2 2 0 0012 14a2 2 0 001.4-.6M9.9 5.1A9.8 9.8 0 0112 5c5 0 9 4 10 7-0.4 1.1-1.2 2.4-2.3 3.5M6.1 6.1C4.4 7.4 3.2 9.1 2 12c1.1 3 5.1 7 10 7 1.4 0 2.7-.3 3.9-.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className={layout === 'column' ? 'inline' : 'hidden sm:inline'}>Hide</span>
      </ToolBtn>

      {layout === 'row' && <div className="w-px h-4 bg-yellow-600/25 mx-0.5 shrink-0" />}

      <ToolBtn
        layout={layout}
        title="Orbit / fly around"
        active={flyAround}
        onClick={onToggleFlyAround}
      >
        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.7">
          <circle cx="12" cy="12" r="3" />
          <path d="M12 5a9 9 0 018 5.2M12 19a9 9 0 01-8-5.2" strokeLinecap="round" />
        </svg>
        <span className={layout === 'column' ? 'inline' : 'hidden sm:inline'}>Orbit</span>
      </ToolBtn>

      <ToolBtn layout={layout} title="Plan grid (G)" active={showGrid} onClick={onToggleGrid}>
        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.7">
          <path d="M4 4h16v16H4zM4 12h16M12 4v16" />
        </svg>
        <span className={layout === 'column' ? 'inline' : 'hidden sm:inline'}>Grid</span>
      </ToolBtn>

      <ToolBtn
        layout={layout}
        title="Section / slice scene (X)"
        active={sectionPanelOpen || sectionEnabled}
        onClick={() => {
          const section = useSectionStore.getState();
          const wasActive = section.panelOpen || section.enabled;
          section.togglePanel();
          if (wasActive) setClickType('pan');
        }}
      >
        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.7">
          <circle cx="6" cy="6" r="2.5" />
          <circle cx="6" cy="18" r="2.5" />
          <path d="M20 4L8.5 15.5M8.5 8.5L20 20" strokeLinecap="round" />
        </svg>
        <span className={layout === 'column' ? 'inline' : 'hidden sm:inline'}>Section</span>
      </ToolBtn>

      {getLevelLabels()?.levels?.length > 0 && (
        <>
          {layout === 'row' && <div className="w-px h-4 bg-yellow-600/25 mx-0.5 shrink-0" />}

          <ToolBtn
            layout={layout}
            title="Labels (B)"
            active={showLabels}
            onClick={() => setLabels()}
          >
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.7">
              <path d="M4 6h16M4 12h16M4 18h10" strokeLinecap="round" />
            </svg>
            <span className={layout === 'column' ? 'inline' : 'hidden sm:inline'}>Labels</span>
          </ToolBtn>
        </>
      )}

      {layout === 'row' && <div className="w-px h-4 bg-yellow-600/25 mx-0.5 shrink-0" />}

      <ToolBtn layout={layout} title="Shortcuts (?)" active={showHelp} onClick={onHelp}>
        <span className="text-[11px] font-bold leading-none">?</span>
        <span className={layout === 'column' ? 'inline' : 'hidden sm:inline'}>Help</span>
      </ToolBtn>

      {layout === 'row' && <div className="w-px h-4 bg-yellow-600/25 mx-0.5 shrink-0" />}

      <ToolBtn
        layout={layout}
        title="Plan view, north up (N)"
        active={planLocked}
        onClick={() => togglePlanLocked()}
      >
        <span className={layout === 'column' ? 'inline' : 'hidden sm:inline'}>Plan</span>
        <span className={layout === 'column' ? 'hidden' : 'sm:hidden inline'}>Plan</span>
      </ToolBtn>
      <ToolBtn
        layout={layout}
        title="Snap clicks to collars and 1 m grid"
        active={snapEnabled}
        onClick={() => toggleSnapEnabled()}
      >
        <span>Snap</span>
      </ToolBtn>
      <ToolBtn
        layout={layout}
        title="Digitise a 3D string in mine coordinates"
        active={digitizing}
        onClick={() => {
          const store = useStringLayerStore.getState();
          if (store.digitizing) store.finishDigitize();
          else store.startDigitize();
        }}
      >
        <span>{digitizing ? 'Done str' : 'Digitise'}</span>
      </ToolBtn>
      <ToolBtn layout={layout} title="Import CSV or DXF strings" onClick={() => fileRef.current?.click()}>
        <span>Import</span>
      </ToolBtn>
      <ToolBtn
        layout={layout}
        title="Export strings as CSV"
        onClick={() => {
          const csv = stringsToCsv(useStringLayerStore.getState().strings);
          downloadBlob(new Blob([csv], { type: 'text/csv' }), 'mdv-strings.csv');
        }}
      >
        <span>CSV</span>
      </ToolBtn>
      <ToolBtn
        layout={layout}
        title="Export strings as DXF"
        onClick={() => {
          const dxf = stringsToDxf(useStringLayerStore.getState().strings);
          downloadBlob(new Blob([dxf], { type: 'application/dxf' }), 'mdv-strings.dxf');
        }}
      >
        <span>DXF</span>
      </ToolBtn>
      {stringCount > 0 ? (
        <ToolBtn
          layout={layout}
          title="Remove all imported / digitised strings"
          onClick={() => useStringLayerStore.getState().clearStrings()}
        >
          <span>Clear str</span>
        </ToolBtn>
      ) : null}
      <input
        ref={fileRef}
        type="file"
        accept=".csv,.txt,.dxf"
        className="hidden"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (!file) return;
          const text = await file.text();
          const rows = parseStringFile(file.name, text);
          useStringLayerStore.getState().addStrings(rows);
        }}
      />

      {layout === 'row' && <div className="w-px h-4 bg-yellow-600/25 mx-0.5 shrink-0" />}
      <MeasurementList layout={layout} />
    </ToolSubmenu>
  );
}
